// src/components/ZoneHazardPopup.jsx
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";

const API_BASE_URL = "https://formbuilderapi.getforsite.com";

const statusMeta = (s) => {
  const map = {
    0: { label: "Inactive", color: "#5a5a5a", bg: "rgba(158,158,158,0.12)" },
    1: { label: "Active", color: "#11804b", bg: "rgba(0,200,83,0.08)" },
    2: { label: "Archived", color: "#5a5a5a", bg: "rgba(117,117,117,0.12)" },
    3: { label: "Draft", color: "#0f6f94", bg: "rgba(33,150,243,0.10)" },
  };
  return map?.[s] ?? { label: String(s), color: "#333", bg: "#eee" };
};

const riskMeta = (r) => {
  const map = {
    1: { label: "Low", color: "#11804b", bg: "rgba(0,200,83,0.12)" }, // green
    2: { label: "Medium", color: "#9a5c00", bg: "rgba(255,154,2,0.16)" }, // orange
    3: { label: "High", color: "#a10000", bg: "rgba(208,0,0,0.12)" }, // red
    4: { label: "Extreme", color: "#721c24", bg: "rgba(255,90,90,0.16)" },
    5: { label: "Severe", color: "#721c24", bg: "rgba(208,0,0,0.12)" },
  };
  return map?.[r] ?? { label: "Unknown", color: "#555", bg: "#eee" };
};

const ratingColor = (r) => {
  switch (r) {
    case 1:
      return "#00c853"; // Low = green
    case 2:
      return "#ff9a02"; // Medium = orange
    case 3:
      return "#d00000"; // High = red
    case 4:
      return "#d00000"; // Extreme = red
    case 5:
      return "#d00000"; // Severe = red
    default:
      return "#ddd";
  }
};

const ZoneHazardPopup = ({ open, onClose, siteId, zoneName }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [viewer, setViewer] = useState({ open: false, src: "" });

  useEffect(() => {
    if (!open || !siteId || !zoneName) return;
    let cancelled = false;

    const fetchHazards = async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/hazards/getZoneHazardRegister`,
          { params: { siteId, name: zoneName } },
        );
        if (!cancelled) setHazards(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled)
          setErr(e?.response?.data?.error || "Failed to load hazards.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHazards();
    return () => {
      cancelled = true;
    };
  }, [open, siteId, zoneName]);

  const visibleHazards = useMemo(() => {
    return [...hazards].sort((a, b) => {
      const rA = a?.rating ?? -1;
      const rB = b?.rating ?? -1;
      if (rA !== rB) return rB - rA;
      return String(a?.area || "").localeCompare(String(b?.area || ""));
    });
  }, [hazards]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "16px 20px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Typography variant="h6">
            Zone Hazard Register — {zoneName || "Zone"}
          </Typography>
          <Button
            onClick={onClose}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#ffeb66",
              border: "1px solid #ffeb66",
              color: "#111",
              "&:hover": { backgroundColor: "#ffd902", color: "#fff" },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent dividers sx={{ p: 2, background: "#fff" }}>
        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={3}
          >
            <CircularProgress />
          </Box>
        )}
        {!!err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}
        {!loading && !err && visibleHazards.length === 0 && (
          <Typography align="center" color="text.secondary" py={3}>
            No hazards found for this zone.
          </Typography>
        )}

        {!loading &&
          !err &&
          visibleHazards.map((h, idx) => {
            // inside visibleHazards.map(...)
            const rating = Number(h?.rating) || 0;
            const risk = riskMeta(rating);
            const status = statusMeta(h?.status);

            const images =
              (Array.isArray(h?.media?.images) && h.media.images) || [];

            return (
              <Box
                key={h.id ?? `haz-${idx}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 220px" },
                  gap: 2,
                  p: 2,
                  border: "1px solid #ececec",
                  borderLeft: `6px solid ${ratingColor(rating)}`,
                  borderRadius: "12px",
                  background: "#fafafa",
                  mb: 2,
                }}
              >
                {/* Left content */}
                <Box>
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}
                  >
                    {/* Status pill */}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: status.bg,
                        color: status.color,
                      }}
                    >
                      {status.label}
                    </Box>

                    {/* Rating pill */}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: risk.bg,
                        color: risk.color,
                      }}
                    >
                      {risk.label}
                    </Box>

                    {h?.location && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Location:</strong> {h.location}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ borderTop: "1px dashed #eaeaea", pt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Hazard
                    </Typography>
                    <Typography variant="body2">{h.hazard || "—"}</Typography>
                  </Box>
                  <Box sx={{ borderTop: "1px dashed #eaeaea", pt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Control
                    </Typography>
                    <Typography variant="body2">{h.measure || "—"}</Typography>
                  </Box>
                  {h?.area && (
                    <Box sx={{ borderTop: "1px dashed #eaeaea", pt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Area
                      </Typography>
                      <Typography variant="body2">{h.area}</Typography>
                    </Box>
                  )}
                  {h?.type && (
                    <Box sx={{ borderTop: "1px dashed #eaeaea", pt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body2">{h.type}</Typography>
                    </Box>
                  )}
                </Box>

                {/* Right: gallery */}
                <Box
                  sx={{
                    borderLeft: { md: "1px solid #eee" },
                    pl: { md: 2 },
                    pt: { xs: 2, md: 0 },
                  }}
                >
                  {images.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      {images.slice(0, 6).map((src, i) => (
                        <Box
                          key={i}
                          component="button"
                          onClick={() => setViewer({ open: true, src })}
                          sx={{
                            border: 0,
                            p: 0,
                            cursor: "pointer",
                            borderRadius: 1,
                            overflow: "hidden",
                            boxShadow: 1,
                            backgroundImage: `url(${src})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            width: "100%",
                            pt: "70%",
                          }}
                        />
                      ))}
                      {images.length > 6 && (
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{ mt: 1 }}
                        >
                          +{images.length - 6} more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No images
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
      </DialogContent>

      {/* Inline viewer */}
      {viewer.open && (
        <Box
          sx={{
            position: "fixed",
            inset: "10%",
            background: "rgba(0,0,0,0.75)",
            display: "grid",
            placeItems: "center",
            zIndex: 1302,
            borderRadius: 2,
          }}
        >
          <Button
            onClick={() => setViewer({ open: false, src: "" })}
            sx={{
              position: "absolute",
              top: 8,
              right: 12,
              minWidth: 0,
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#fff",
              color: "#000",
              fontSize: 20,
              fontWeight: "bold",
              "&:hover": { bgcolor: "#eee" },
            }}
          >
            ×
          </Button>
          <a href={viewer.src} target="_blank" rel="noreferrer">
            <Box
              component="img"
              src={viewer.src}
              alt="Hazard"
              sx={{
                maxWidth: "90%",
                maxHeight: "80%",
                borderRadius: 1,
                bgcolor: "#fff",
              }}
            />
          </a>
        </Box>
      )}
    </Dialog>
  );
};

ZoneHazardPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  zoneName: PropTypes.string.isRequired,
};

export default React.memo(ZoneHazardPopup);
